/**
 * Grants domain — React Query hooks.
 *
 * Read hooks are pass-through re-exports of the generated client. The
 * runtime response of `GET /nylas/grants/` matches DRF's standard
 * paginated shape, so no hand-written list wrapper is needed.
 *
 * Write hooks go through `useInvalidatingMutation` so cache invalidation
 * is declarative and compile-time-enforced via the `GrantsMutationId`
 * registry in `./invalidation`.
 */

import { deleteGrant } from '../generated/clients/deleteGrant'
import { updateGrantPreferences } from '../generated/clients/updateGrantPreferences'
import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'

import type { DeleteGrantPathParams } from '../generated/types/DeleteGrant'
import type {
  UpdateGrantPreferencesMutationRequest,
  UpdateGrantPreferencesPathParams,
} from '../generated/types/UpdateGrantPreferences'

// ── Generated query-hook re-exports ────────────────────────────────

export {
  useListGrants,
  listGrantsQueryKey,
} from '../generated/hooks/grantsController'

// ── Generated query/mutation parameter & response type re-exports ──

export type {
  ListGrantsQueryParams,
  ListGrantsQueryResponse,
} from '../generated/types/ListGrants'
export type {
  UpdateGrantPreferencesMutationRequest,
  UpdateGrantPreferencesMutationResponse,
  UpdateGrantPreferencesPathParams,
} from '../generated/types/UpdateGrantPreferences'
export type {
  DeleteGrantMutationResponse,
  DeleteGrantPathParams,
} from '../generated/types/DeleteGrant'

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

export function useUpdateGrantPreferences() {
  return useInvalidatingMutation(
    'updateGrantPreferences',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        grant_id: UpdateGrantPreferencesPathParams['grant_id']
        data: UpdateGrantPreferencesMutationRequest
      }) =>
        updateGrantPreferences(
          { grant_id: variables.grant_id },
          variables.data
        ),
    }
  )
}

export function useDeleteGrant() {
  return useInvalidatingMutation('deleteGrant', invalidationRegistry, {
    mutationFn: (variables: { grant_id: DeleteGrantPathParams['grant_id'] }) =>
      deleteGrant({ grant_id: variables.grant_id }),
  })
}
